:host {
  display: inline-block;
  width: auto;
  cursor: pointer;
}
:host([full]) {
  display: block;
}

.button {
  display: inline-flex;
  align-items: stretch;
  justify-content: center;
  width: 100%;
  border-style: solid;
  border-width: var(--presto-player-button-border-width);
  font-family: var(--plyr-font-family, inherit);
  font-weight: var(--presto-player-button-font-weight, 500);
  text-decoration: none;
  user-select: none;
  white-space: nowrap;
  vertical-align: middle;
  padding: 0;
  transition: var(--presto--player-transition-fast, 150ms) background-color, var(--presto-player-transition-fast, 150ms) color, var(--presto-player-transition-fast, 150ms) border,
    var(--presto--player-transition-fast, 150ms) box-shadow;
  cursor: inherit;
  border-radius: var(--presto-player-button-border-radius, 0.25em);

  &::-moz-focus-inner {
    border: 0;
  }

  &:focus {
    outline: none;
  }

  &.button--disabled {
    opacity: 0.5;
    cursor: not-allowed;

    // When disabled, prevent mouse events from bubbling up
    * {
      pointer-events: none;
    }
  }

  // Clicks on icons shouldn't prevent the button from gaining focus
  ::slotted(.presto--icon) {
    pointer-events: none;
  }
}

.button__prefix,
.button__suffix {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
}

.button__label {
  display: flex;
  align-items: center;

  ::slotted(presto-icon) {
    vertical-align: -2px;
  }
}

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Standard buttons
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

.button {
  &.button--default {
    background-color: var(--presto-player-color-white, #fff);
    border-color: var(--presto-player-color-gray-300, #d1d5db);
    color: var(--presto-player-color-gray-600, #4b5563);

    &:hover:not(.button--disabled) {
      border-color: var(--presto-player-button-color, var(--plyr-color-main));
      color: var(--presto-player-button-color, var(--plyr-color-main));
      opacity: 0.75;
    }

    &:focus:not(.button--disabled) {
      border-color: var(--presto-player-button-color, var(--plyr-color-main));
      color: var(--presto-player-button-color, var(--plyr-color-main));
      opacity: 1;
    }

    &:active:not(.button--disabled) {
      border-color: var(--presto-player-button-color, var(--plyr-color-main));
      color: var(--presto-player-button-color, var(--plyr-color-main));
      opacity: 1;
    }
  }

  &.button--primary {
    background-color: var(--presto-player-button-color, var(--plyr-color-main));
    border-color: var(--presto-player-button-color, var(--plyr-color-main));
    color: var(--presto-player-button-text, #fff);

    &:hover:not(.button--disabled) {
      opacity: 0.9;
    }

    &:focus:not(.button--disabled) {
      opacity: 1;
    }

    &:active:not(.button--disabled) {
      opacity: 1;
    }
  }
}

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Text buttons
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

.button--text {
  background-color: transparent;
  border-color: transparent;
  color: var(--presto-player-button-color, var(--plyr-color-main));

  &:hover:not(.button--disabled) {
    background-color: transparent;
    border-color: transparent;
    opacity: 0.8;
  }

  &:focus:not(.button--disabled) {
    background-color: transparent;
    border-color: transparent;
    opacity: 0.7;
  }

  &:active:not(.button--disabled) {
    background-color: transparent;
    border-color: transparent;
    opacity: 0.7;
  }
}

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Size modifiers
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

.button--small {
  font-size: var(--presto-player-button-font-size-small, 0.875em);
  height: var(--presto-player-button-height-small, 1.875em);
  line-height: calc(var(--presto-player-button-height-small, 1.875em) - var(--presto-player-button-border-width, 1px) * 2);
}

.button--medium {
  font-size: var(--presto-player-button-font-size-medium, 1em);
  height: var(--presto-player-button-height-medium, 2.5em);
  line-height: calc(var(--presto-player-button-height-medium, 2.5em) - var(--presto-player-button-border-width, 1px) * 2);
}

.button--large {
  font-size: var(--presto-player-button-font-size-large, 1.25em);
  height: var(--presto-player-button-height-large, 3.125em);
  line-height: calc(var(--presto-player-button-height-large, 3.125em) - var(--presto-player-button-border-width, 1px) * 2);
}

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Full modifier
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
.button--full {
  display: block;
}

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Button spacing
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

.button--has-label {
  &.button--small .button__label {
    padding: 0 var(--presto-player-button-spacing-small, 0.75em);
  }

  &.button--medium .button__label {
    padding: 0 var(--presto-player-button-spacing-medium, 1em);
  }

  &.button--large .button__label {
    padding: 0 var(--presto-player-button-spacing-large, 1.25em);
  }
}

.button--has-prefix {
  &.button--small {
    padding-left: var(--presto-player-button-spacing-x-small, 0.5em);

    .button__label {
      padding-left: var(--presto-player-button-spacing-x-small, 0.5em);
    }
  }

  &.button--medium {
    padding-left: var(--presto-player-button-spacing-small, 0.75em);

    .button__label {
      padding-left: var(--presto-player-button-spacing-small, 0.75em);
    }
  }

  &.button--large {
    padding-left: var(--presto-player-button-spacing-small, 0.75em);

    .button__label {
      padding-left: var(--presto-player-button-spacing-small, 0.75em);
    }
  }
}

.button--has-suffix,
.button--caret {
  &.button--small {
    padding-right: var(--presto-player-button-spacing-x-small, 0.5em);

    .button__label {
      padding-right: var(--presto-player-button-spacing-x-small, 0.5em);
    }
  }

  &.button--medium {
    padding-right: var(--presto-player-button-spacing-small, 0.75em);

    .button__label {
      padding-right: var(--presto-player-button-spacing-small, 0.75em);
    }
  }

  &.button--large {
    padding-right: var(--presto-player-button-spacing-small, 0.75em);

    .button__label {
      padding-right: var(--presto-player-button-spacing-small, 0.75em);
    }
  }
}
